import { difference } from 'lodash';

import { chartColors } from '../../../Common/Charts/enums/chart-colors.enum';
import { ThemeApi } from '../../../api/social/model/theme.api.model';
import { BaseModel } from '../../../Common/Basic/models/base.model';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';

export class Theme extends BaseModel<Theme, ThemeApi> implements AuditableModel {

    static readonly MAX_NUM_AGENTS: number = 12;

    id: number;
    name: string;
    subscriptionIds: number[] = [];

    avatarUrl: string;
    avatarPlaceholderText: string;
    avatarPlaceholderBackgroundColor: string;
    avatarPlaceholderColor: string = chartColors.agprWhite;

    expanded?: boolean;

    createdUserId: number;
    modifiedUserId: number;
    createdDate: Date;
    modifiedDate: Date;

    // uses default chart palette
    readonly avatarPlaceholderColorOptions: chartColors[] = [
        chartColors.agprBlue,
        chartColors.agprPurple,
        chartColors.agprLightBlue,
        chartColors.agprOrange,
        chartColors.agprYellow,
        chartColors.agprLightGreen,
        chartColors.agprGreen,
        chartColors.agprTeal,
        chartColors.agprMediumBlue,
        chartColors.agprDarkPurple
    ];

    constructor(apiModel?: ThemeApi) {
        super();
        if (apiModel) {
            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }
    }

    constructFromApiModel(apiModel: ThemeApi) {
        this.id = apiModel.id;
        this.name = apiModel.name;
        this.subscriptionIds = apiModel.subscriptionIds;

        if (apiModel.avatarUrl) {
            this.avatarUrl = apiModel.avatarUrl;
        } else {
            this.avatarPlaceholderBackgroundColor = this.avatarPlaceholderColorOptions[this.id % this.avatarPlaceholderColorOptions.length];
            this.avatarPlaceholderText = this.prepareAvatarPlaceholderText(this.name);
        }

        this.createdUserId = apiModel.createdUserId;
        this.createdDate = apiModel.createdDate ? new Date(apiModel.createdDate) : null;

        this.modifiedUserId  = apiModel.modifiedUserId;
        this.modifiedDate = apiModel.modifiedDate ? new Date(apiModel.modifiedDate) : null;
    }

    constructDefault() {
        this.id = null;
        this.name = '';
        this.subscriptionIds = [];

        this.avatarUrl = null;
        this.avatarPlaceholderText = '';

        this.createdDate = null;
        this.createdUserId = null;

        this.modifiedDate = null;
        this.modifiedUserId  = null;
    }


    toApiModel(): ThemeApi {
        const apiModel: ThemeApi = {
            id: this.id,
            name: this.name,
            subscriptionIds: this.subscriptionIds,
            avatarUrl: this.avatarUrl,

            createdDate: this.createdDate,
            createdUserId: this.createdUserId,

            modifiedUserId: this.modifiedUserId,
            modifiedDate: this.modifiedDate,
        };
        return apiModel;
    }

    clone(): Theme {
        return new Theme(this.toApiModel());
    }

    equals(other: Theme): boolean {
        if (!other) {
            return false;
        }

        if (this.name !== other.name) {
            return false;
        }

        if (difference(this.subscriptionIds, other.subscriptionIds)?.length > 0) {
            return false;
        }

        if (this.avatarUrl !== other.avatarUrl) {
            return false;
        }

        if (this.createdUserId !== other.createdUserId) {
            return false;
        }

        if (this.createdDate?.getTime() !== other.createdDate?.getTime()) {
            return false;
        }

        if (this.modifiedUserId !== other.modifiedUserId) {
            return false;
        }

        if (this.modifiedDate?.getTime() !== other.modifiedDate?.getTime()) {
            return false;
        }

        return true;
    }


    /**
     * Convert the theme name to a 2 character string
     */
    private prepareAvatarPlaceholderText(themeName: string): string {
        const trimmed = themeName.trim();

        if (!trimmed) {
            return '??';
        }

        const themeNameSplit = trimmed.split(' ');

        if (themeNameSplit.length === 1) {
            // take the first 2 char of the singular word
            return this.name.slice(0, Math.min(themeNameSplit[0].length, 2)).toLocaleUpperCase();
        } else {
            // use the first char of the first and last word
            return themeNameSplit[0][0].toLocaleUpperCase() + themeNameSplit[themeNameSplit.length - 1][0].toLocaleUpperCase();
        }
    }
}
